<script setup lang="ts">
import axios from "axios";
import { useMutation } from "@tanstack/vue-query";
import { UserX } from "lucide-vue-next";
import { useGoogleCleanupStore } from "@/stores/googleCleanup";
import { useUiStore } from "@/stores/ui";
import { deleteLeadGoogleContact } from "@/api/leads";

// Dialog HITL globalny (docs/integrations/pilot-contacts.md §4/F-2) — zamontowany raz w App.vue,
// otwierany eventem `lead:google-contact-prompt` z api/leads.ts. "Tak" = dokładnie 1 wywołanie
// delete; "Nie"/Esc/klik w tło = 0 wywołań; pending blokuje drugi klik (anty-double-click).
const store = useGoogleCleanupStore();
const ui = useUiStore();

const mut = useMutation({
  mutationFn: (id: number) => deleteLeadGoogleContact(id),
  onSuccess: (res) => {
    if (res.status === "deleted" || res.status === "not_found") {
      ui.pushToast("success", "Kontakt Google usunięty");
    } else if (res.status === "ambiguous") {
      ui.pushToast("warn", "Niejednoznaczne dopasowanie kontaktu — usuń ręcznie w Google Contacts");
    } else if (res.status === "no_google_session") {
      ui.pushToast("info", "Doradca nie ma aktywnej sesji Google w Pilocie — kontakt nie został usunięty");
    }
    store.close();
  },
  onError: (err) => {
    // 409 (stan się zmienił) / 503 (integracja wyłączona) — zamknij, dalsze próby bez sensu.
    // Inne (502 Pilot nieosiągalny, sieć) — dialog zostaje, użytkownik może kliknąć "Ponów".
    const status = axios.isAxiosError(err) ? err.response?.status : undefined;
    if (status === 409) {
      ui.pushToast("warn", "Stan leada zmienił się w międzyczasie — sprawdź etap i spróbuj ponownie");
      store.close();
    } else if (status === 503) {
      ui.pushToast("info", "Integracja z Pilotem nie jest skonfigurowana");
      store.close();
    } else {
      ui.pushToast("error", "Nie udało się usunąć kontaktu — spróbuj ponownie");
    }
  },
});

function onConfirm(): void {
  if (store.leadId !== null && !mut.isPending.value) mut.mutate(store.leadId);
}

function onCancel(): void {
  if (!mut.isPending.value) store.close();
}
</script>

<template>
  <div v-if="store.leadId !== null" class="modal-backdrop" @click.self="onCancel">
    <div class="modal" role="dialog" aria-modal="true" aria-labelledby="gcc-title">
      <header class="modal-head">
        <h2 id="gcc-title">Usunąć kontakt Google?</h2>
      </header>

      <div class="modal-body">
        <p>
          Ten lead rekrutacyjny został oznaczony jako przegrany. Czy usunąć kontakt tej osoby
          z kontaktów Google doradcy? Operacji nie można cofnąć z poziomu CRM.
        </p>
      </div>

      <footer class="modal-foot">
        <button class="btn btn-ghost" :disabled="mut.isPending.value" @click="onCancel">Nie</button>
        <button class="btn btn-gold" :disabled="mut.isPending.value" @click="onConfirm">
          <UserX :size="16" :stroke-width="2" />
          {{ mut.isPending.value ? "Usuwam…" : "Tak, usuń" }}
        </button>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100;
  background: color-mix(in srgb, var(--brand-navy-deep) 45%, transparent);
  display: grid;
  place-items: center;
  padding: 20px;
}
.modal {
  width: 100%;
  max-width: 440px;
  background: var(--bg-overlay);
  border: 1px solid var(--line);
  border-radius: var(--r-box);
  box-shadow: var(--shadow-3);
  display: flex;
  flex-direction: column;
}
.modal-head {
  padding: 18px 20px;
  border-bottom: 1px solid var(--line-soft);
}
.modal-head h2 {
  font-family: var(--font-serif);
  font-size: var(--fs-xl);
  font-weight: 500;
  color: var(--ink-primary);
  margin: 0;
}
.modal-body { padding: 20px; color: var(--ink-secondary); font-size: var(--fs-sm); line-height: 1.5; }
.modal-body p { margin: 0; }
.modal-foot {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid var(--line-soft);
}
.modal-foot .btn { display: inline-flex; align-items: center; gap: 6px; }
</style>
